import React, { useState } from 'react';

import { ToolTip } from '../ToolTip/ToolTip';
import { Box } from '../Box/Box';
import { Selectable } from '../Selectable/Selectable';
import { CountryLanguageItem } from './CountryLanguageItem/CountryLanguageItem';
import { useComponentVisible } from '../../../hooks/useComponentVisible/useComponentVisible';
import { supportedCountries } from './utils';
import styles from './CountryLanguageSwitcher.module.scss';
import { splitLocale } from '../../../redux/modules/intl/utils';
import { CountryUtils } from '../../../utils/CountryUtils';
import { TooltipPlacementType } from '../ToolTip/types';

interface Props {
  country: string;
  language: string;
  withPhoneCode?: boolean;
  handleChange: (propName: string, value: string) => void;
  button: React.ReactElement;
  menuPlacement?: TooltipPlacementType;
}

const CountryLanguageSwitcher: React.FC<Props> = ({
  country,
  language,
  handleChange,
  withPhoneCode = true,
  button,
  menuPlacement = 'right',
}) => {
  const [selectedLocale, setSelectedLocale] = useState<string>(
    `${language}_${country}`,
  );
  const { ref, isVisible, setIsVisible } = useComponentVisible(false);

  const handleChangeCountryAndLanguage = (
    country: string,
    language: string,
  ) => {
    if (withPhoneCode) {
      const selectedCountry = supportedCountries.find(
        item => item.countryCode === country,
      );
      const phoneCode = selectedCountry?.phoneCode;
      phoneCode && handleChange('phoneCode', phoneCode);
    }

    handleChange('locale', `${language}_${country}`);
  };

  const handleSelect = (locale: string) => {
    setSelectedLocale(locale);
    const { language, country } = splitLocale(locale);
    handleChangeCountryAndLanguage(country, language);
    setIsVisible(false);
  };

  const handleSetIsVisible = () => {
    setIsVisible(true);
  };

  return (
    <ToolTip
      placement={menuPlacement}
      popupContent={
        <div ref={ref}>
          <Box
            width={235}
            hpadding={'no'}
            vpadding={'no'}
            className={styles.box}
          >
            {supportedCountries.map(item => (
              <Selectable
                key={item.locale}
                selected={item.locale === selectedLocale}
                onClick={() => handleSelect(item.locale)}
              >
                <CountryLanguageItem
                  showLanguage={
                    item.countryCode === CountryUtils.COUNTRY.BELGIUM ||
                    item.countryCode === CountryUtils.COUNTRY.INDIA
                  }
                  languageOnRight
                  size={'small'}
                  countryTranslationKey={item.countryTranslationKey}
                  languageTranslationKey={item.languageTranslationKey}
                  countryCode={item.countryCode}
                />
              </Selectable>
            ))}
          </Box>
        </div>
      }
      trigger={'click'}
      className={styles.tooltip}
      visible={isVisible}
    >
      {React.cloneElement(button, { onClick: handleSetIsVisible })}
    </ToolTip>
  );
};

export { CountryLanguageSwitcher };
